/**
 * Auth Context for SAMA Member Portal
 * Supports Email/Password + Google OAuth
 * Includes CSRF token management
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setCsrfToken } from '../lib/api';

const AuthContext = createContext(null);
// Use REACT_APP_BACKEND_URL when set (split-host AWS deploy), otherwise fall back
// to relative '/api' so dev (CRA proxy) and same-origin deployments keep working.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export function AuthProvider({ children }) {
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const checkAuth = useCallback(async () => {
    if (authChecked) return;
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/auth/me`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const memberData = await response.json();
        setMember(memberData);
        setIsAuthenticated(true);
        // Consume the rolled CSRF token from /auth/me so it's available in
        // memory + localStorage immediately, regardless of whether the
        // browser exposed the cookie to JS (e.g. on first paint after a
        // hard reload, or in split-host SameSite=None setups).
        if (memberData.csrf_token) {
          setCsrfToken(memberData.csrf_token);
        } else {
          // Fallback: re-read from cookie if backend didn't return one.
          setCsrfToken(null);
        }
      } else if (response.status === 401 || response.status === 403) {
        // Genuine auth failure — session expired / not logged in.
        setMember(null);
        setIsAuthenticated(false);
      } else {
        // 5xx / 408 / 429 — transient backend issue (Supabase outage,
        // gateway timeout, rate limit, etc.). Don't flip the user to
        // "logged out" — they were authenticated a second ago and a
        // single failed health check shouldn't bounce them to /login.
        // We just leave the previous auth state untouched; the next
        // periodic /auth/me ping or any in-app interaction will retry.
        console.warn(`[Auth] /auth/me transient ${response.status} — keeping previous session state`);
      }
    } catch (error) {
      // Network error (DNS, offline, fetch abort, etc.). Same rationale
      // as the 5xx branch above — don't aggressively log the user out
      // just because their wifi blipped or the API gateway briefly
      // refused a connection.
      console.warn('[Auth] /auth/me network error — keeping previous session state:', error?.message || error);
    } finally {
      setIsLoading(false);
      setAuthChecked(true);
    }
  }, [authChecked]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Called after successful login (email/password or Google)
  const login = async (emailOrUserData, password) => {
    // If called with two arguments, it's email/password login
    if (password !== undefined) {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: emailOrUserData, password }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Login failed');
      }
      
      const userData = await response.json();
      setMember(userData);
      setIsAuthenticated(true);
      setAuthChecked(true);
      if (userData.csrf_token) {
        setCsrfToken(userData.csrf_token);
      } else {
        setCsrfToken(null);
      }
      return userData;
    }
    
    // Otherwise, it's a direct user data object (from Google login)
    const userData = emailOrUserData;
    setMember(userData);
    setIsAuthenticated(true);
    setAuthChecked(true);
    // Set CSRF token from login response
    if (userData.csrf_token) {
      setCsrfToken(userData.csrf_token);
    } else {
      // Fallback to reading from cookie
      setCsrfToken(null);
    }
    return userData;
  };

  const logout = async (redirectTo = '/') => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Always clear local state
      setMember(null);
      setIsAuthenticated(false);
      setAuthChecked(false);
      // Clear CSRF token
      setCsrfToken(null);
      window.location.href = redirectTo;
    }
  };

  const updateMember = (updates) => {
    setMember(prev => ({ ...prev, ...updates }));
  };

  // Refresh auth state (useful after operations that might change user data)
  const refreshAuth = async () => {
    setAuthChecked(false);
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{
      member,
      isLoading,
      isAuthenticated,
      permissions: member?.permissions || {},
      login,
      logout,
      checkAuth,
      refreshAuth,
      updateMember,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  // Derived: true for super admin OR any role-holder with at least one permission.
  // Use this for "can access admin panel" gating instead of bare `is_admin`.
  const isAdminish = Boolean(
    context.member?.is_admin ||
    Object.keys(context.member?.permissions || {}).length > 0
  );
  // Alias 'member' as 'user' for convenience
  return {
    ...context,
    user: context.member,
    isAdminish,
  };
}
